import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useKycVaraFlow } from "./useKycVaraFlow";
import { QUESTIONS } from "./content";
import StartStep from "./steps/StartStep";
import AccountTypeStep from "./steps/AccountTypeStep";
import YesNoStep from "./steps/YesNoStep";
import BusinessTypeStep from "./steps/BusinessTypeStep";
import ClassificationStep from "./steps/ClassificationStep";
import StatusStep from "./steps/StatusStep";
import "./kycVara.css";

const MARKETS_PATH = "/market";
/** While this class is on <body>, kycVara.css drops the site header below the modal. */
const BODY_OPEN_CLASS = "kv-modal-open";

/**
 * KYC VARA onboarding as a modal over the current page (blurred background).
 * Each step is one carousel slide: Continue slides forward, Back slides back.
 * Closing (X, Esc or clicking outside) skips verification for now — answers are already saved.
 */
function KycVaraModal({ userDetails, onClose, closeWhenVerified = true }) {
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const flow = useKycVaraFlow({ userDetails, closeWhenVerified, onAlreadyVerified: onClose });
  const { screen, direction, application, answers, busy, actions } = flow;

  // Lock the page behind the modal, lower the site header, move focus in, and close on Esc.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add(BODY_OPEN_CLASS);
    dialogRef.current?.focus();

    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove(BODY_OPEN_CLASS);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  const closeOnBackdropClick = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  const stepProps = { busy, onBack: flow.goBack, onClose, step: flow.stepNumber, totalSteps: flow.totalSteps };

  let slide;
  switch (screen) {
    case "loading":
      slide = (
        <div className="kv-state" aria-busy="true">
          <span className="kv-spinner kv-spinner--large" aria-hidden="true" />
          <span className="visually-hidden">Loading your verification</span>
        </div>
      );
      break;

    case "error":
      slide = (
        <div className="kv-state">
          <i className="ri-wifi-off-line kv-state__icon" aria-hidden="true" />
          <p>We could not load your verification. Please try again.</p>
          <button type="button" className="kv-btn kv-btn--secondary" onClick={flow.reload}>
            Retry
          </button>
          <button type="button" className="kv-link-btn" onClick={onClose}>
            Close
          </button>
        </div>
      );
      break;

    case "start":
      slide = <StartStep reKycReason={flow.reKycReason} onStart={() => flow.goTo("accountType")} onClose={onClose} />;
      break;

    case "accountType":
      slide = (
        <AccountTypeStep
          {...stepProps}
          value={answers.accountType}
          onChange={flow.setAnswer("accountType")}
          onContinue={actions.continueFromAccountType}
        />
      );
      break;

    case "income":
      slide = (
        <YesNoStep
          {...stepProps}
          question={QUESTIONS.income}
          accountType={answers.accountType}
          value={answers.income}
          onChange={flow.setAnswer("income")}
          onContinue={actions.continueFromIncome}
        />
      );
      break;

    case "assets":
      slide = (
        <YesNoStep
          {...stepProps}
          question={QUESTIONS.assets}
          accountType={answers.accountType}
          value={answers.assets}
          onChange={flow.setAnswer("assets")}
          onContinue={actions.continueFromAssets}
          continueLabel={answers.accountType === "INDIVIDUAL" ? "See my classification" : "Continue"}
        />
      );
      break;

    case "businessType":
      slide = (
        <BusinessTypeStep
          {...stepProps}
          value={answers.businessType}
          onChange={flow.setAnswer("businessType")}
          onContinue={actions.continueFromBusinessType}
        />
      );
      break;

    case "result":
      slide = (
        <ClassificationStep {...stepProps} application={application} onStartVerification={actions.startVerification} />
      );
      break;

    case "status":
      slide = (
        <StatusStep
          application={application}
          canRefresh={flow.isWaitingForDecision}
          refreshing={flow.refreshing}
          onRefresh={flow.refreshStatus}
          busy={busy}
          onContinueVerification={actions.startVerification}
          onRestart={actions.restart}
          onExplore={() => {
            onClose();
            navigate(MARKETS_PATH);
          }}
          onClose={onClose}
        />
      );
      break;

    default:
      slide = null;
  }

  // Rendered on <body> so no parent stacking context or transform can trap the modal.
  return createPortal(
    <div className="kv-modal" onMouseDown={closeOnBackdropClick}>
      <div
        ref={dialogRef}
        className="kv-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Account verification"
        tabIndex={-1}
      >
        {/* Keyed by screen so every step change replays the slide animation. */}
        <div key={screen} className={`kv-slide kv-slide--${direction}`}>
          {slide}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default KycVaraModal;
